import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/Role.css"; // reuse existing card/table/button styles

/**
 * RoleManager (v3.1 UI refresh with RBAC)
 * - List roles (GET /api/roles)
 * - Create role (POST /api/roles) [ADMIN ONLY]
 * - Inline edit (PUT /api/roles/:id) [ADMIN ONLY]
 * - Delete (DELETE /api/roles/:id) [ADMIN ONLY]
 * - Search/filter client-side
 * - Better error handling, duplicate detection, and 404 hints
 */
export default function RoleManager() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  const isAdmin =
    user?.role === "admin" ||
    user?.role === "super-admin";

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const API_BASE = "/api/roles"; // ensure your roles router is mounted here

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // create form
  const [form, setForm] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  // edit state (inline)
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", description: "" });
  const [savingId, setSavingId] = useState(null);

  // search
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchRoles() {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(API_BASE, authHeader);
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch roles";
      let hint = "";
      if (status === 404) {
        hint =
          " (Hint: Check that app.use('/api/roles', rolesRouter) is mounted before the SPA catch-all, and that the proxy forwards /api.)";
      }
      setError(`${msg}${hint}`);
    } finally {
      setLoading(false);
    }
  }

  function validateName(name) {
    if (!name.trim()) return "Role name is required";
    if (!/^[a-zA-Z0-9_\- ]{2,40}$/.test(name.trim()))
      return "Use 2–40 letters, numbers, space, _ or - for the role name";
    return "";
  }

  async function handleAddRole(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isAdmin) {
      setError("Not authorized to add roles");
      return;
    }

    const v = validateName(form.name);
    if (v) return setError(v);

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || "",
      };
      const res = await axios.post(API_BASE, payload, authHeader);

      setRoles((prev) => [res.data, ...prev]);
      setSuccess(`Role "${payload.name}" created`);
      setForm({ name: "", description: "" });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add role";
      if (status === 409 || /duplicate key/i.test(msg)) {
        setError("Role name already exists");
      } else if (status === 404) {
        setError(
          msg +
            " (Hint: Did you mount /api/roles on the server and forward /api via dev proxy / Nginx?)"
        );
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(role) {
    if (!isAdmin) return; // UI guard
    setEditingId(role._id);
    setEditDraft({
      name: role.name || "",
      description: role.description || "",
    });
    setSuccess("");
    setError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft({ name: "", description: "" });
  }

  async function saveEdit(roleId) {
    if (!isAdmin) {
      setError("Not authorized to edit roles");
      return;
    }

    const v = validateName(editDraft.name);
    if (v) return setError(v);

    setSavingId(roleId);
    setError("");
    setSuccess("");

    // optimistic update
    const prev = roles;
    const next = roles.map((r) =>
      r._id === roleId ? { ...r, ...editDraft } : r
    );
    setRoles(next);

    try {
      await axios.put(
        `${API_BASE}/${roleId}`,
        {
          name: editDraft.name.trim(),
          description: editDraft.description?.trim() || "",
        },
        authHeader
      );
      setSuccess(`Saved changes to "${editDraft.name.trim()}"`);
      setEditingId(null);
      setEditDraft({ name: "", description: "" });
    } catch (err) {
      setRoles(prev); // rollback
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save role";
      if (status === 409 || /duplicate key/i.test(msg)) {
        setError("Role name already exists");
      } else if (status === 404) {
        setError(
          msg +
            " (Hint: Ensure PUT /api/roles/:id is defined after module.exports = router, and router is mounted.)"
        );
      } else {
        setError(msg);
      }
    } finally {
      setSavingId(null);
    }
  }

  async function deleteRole(roleId) {
    if (!isAdmin) {
      setError("Not authorized to delete roles");
      return;
    }
    if (!window.confirm("Delete this role?")) return;

    setError("");
    setSuccess("");

    // optimistic removal
    const prev = roles;
    setRoles((rs) => rs.filter((r) => r._id !== roleId));

    try {
      await axios.delete(`${API_BASE}/${roleId}`, authHeader);
      setSuccess("Role deleted");
    } catch (err) {
      setRoles(prev); // rollback
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete role";
      if (status === 404) {
        setError(
          msg +
            " (Hint: Ensure DELETE /api/roles/:id exists and router is mounted at /api/roles)"
        );
      } else {
        setError(msg);
      }
    }
  }

  const filtered = roles.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  });

  const totalRoles = roles.length;
  const visibleRoles = filtered.length;

  return (
    <div className="tech-page finance-page role-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Role Manager</h2>
          <p className="page-subtitle">
            Define and maintain system roles for access control.
          </p>
        </div>

        <div className="page-header-right">
          <div className="role-metrics">
            <span className="metric-pill">
              <strong>{visibleRoles}</strong> visible
            </span>
            <span className="metric-pill muted-pill">
              <strong>{totalRoles}</strong> total
            </span>
          </div>

          <div className="actions">
            <input
              type="text"
              className="input-control"
              placeholder="Search roles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn" onClick={fetchRoles} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Info about RBAC access */}
      {!isAdmin && (
        <div className="alert alert-info">
          You have view-only access to roles. Contact an admin to change RBAC
          settings.
        </div>
      )}

      {/* Top row: Add Role (only if admin) */}
      {isAdmin && (
        <div className="card role-card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Add New Role</h3>
              <p className="card-subtitle">
                Create a role that can later be assigned to users (e.g.{" "}
                <code>technician</code>, <code>manager</code>,{" "}
                <code>finance-head</code>).
              </p>
            </div>
          </div>

          <form className="form-grid" onSubmit={handleAddRole}>
            <div className="form-row">
              <label>
                Role Name<span className="req">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. admin, technician, super-admin"
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                maxLength={40}
                required
              />
              <small className="muted">
                2–40 characters. Letters, numbers, space, <code>_</code> or{" "}
                <code>-</code>.
              </small>
            </div>
            <br></br>
            <div className="form-row">
              <label>Description</label>
              <textarea
                placeholder="Optional description for the role"
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={3}
                maxLength={200}
              />
            </div>
            <br></br>
            <div className="form-row form-row-actions">
              <button
                className="btn primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Adding..." : "Add Role"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Roles Table */}
      <div className="card role-card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Existing Roles</h3>
            <p className="card-subtitle">
              Edit or remove roles used in the rest of the system.
            </p>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>#</th>
                <th>Name</th>
                <th>Description</th>
                {isAdmin && <th style={{ width: 220 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={isAdmin ? 4 : 3}
                    style={{ textAlign: "center", padding: "10px 0" }}
                  >
                    {loading
                      ? "Loading..."
                      : query
                      ? "No matches found for this search."
                      : "No roles found. Start by adding one above."}
                  </td>
                </tr>
              )}

              {filtered.map((r, idx) => (
                <tr key={r._id || r.name}>
                  <td>{idx + 1}</td>
                  <td>
                    {isAdmin && editingId === r._id ? (
                      <input
                        type="text"
                        value={editDraft.name}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            name: e.target.value,
                          }))
                        }
                        maxLength={40}
                      />
                    ) : (
                      <span className="role-name-cell">{r.name}</span>
                    )}
                  </td>
                  <td>
                    {isAdmin && editingId === r._id ? (
                      <textarea
                        rows={2}
                        value={editDraft.description}
                        onChange={(e) =>
                          setEditDraft((d) => ({
                            ...d,
                            description: e.target.value,
                          }))
                        }
                        maxLength={200}
                      />
                    ) : (
                      <span className="role-desc-cell">
                        {r.description || "—"}
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      {editingId === r._id ? (
                        <div className="btn-row">
                          <button
                            className="btn primary"
                            onClick={() => saveEdit(r._id)}
                            disabled={savingId === r._id}
                            type="button"
                          >
                            {savingId === r._id ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="btn"
                            onClick={cancelEdit}
                            type="button"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="btn-row">
                          <button
                            className="btn"
                            onClick={() => startEdit(r)}
                            type="button"
                          >
                            Edit
                          </button>
                          <button
                            className="btn danger"
                            onClick={() => deleteRole(r._id)}
                            type="button"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alerts (bottom, so main layout is stable) */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
    </div>
  );
}
